import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  db,
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDocs,
} from '../firebase';
import {
  toYmd,
  getYmdsForEventInMonth,
  eventSortKey,
  formatEventWhen,
  buildCalendarEventFields,
  validateCalendarForm,
  defaultCalendarDraft,
  formatRecurrenceLine,
  formatCalendarReminderSummary,
} from '../lib/calendarEvent';
import { HAYDI_ITEM_COLORS, DEFAULT_ITEM_COLOR, eventColorHex } from '../lib/itemColors';
import { CALENDAR_REMINDER_OFFSET_PRESETS } from '../lib/todoReminders';

/** Monday-first week: 0 = Monday … 6 = Sunday */
function mondayIndex(jsDay) {
  return (jsDay + 6) % 7;
}

function buildMonthGrid(viewMonth) {
  const y = viewMonth.getFullYear();
  const m = viewMonth.getMonth();
  const first = new Date(y, m, 1);
  const lastDay = new Date(y, m + 1, 0).getDate();
  const padStart = mondayIndex(first.getDay());
  const cells = [];

  const prevEnd = new Date(y, m, 0).getDate();
  for (let i = 0; i < padStart; i += 1) {
    const day = prevEnd - padStart + i + 1;
    const date = new Date(y, m - 1, day);
    cells.push({ kind: 'other', date, ymd: toYmd(date) });
  }
  for (let d = 1; d <= lastDay; d += 1) {
    const date = new Date(y, m, d);
    cells.push({ kind: 'current', date, ymd: toYmd(date) });
  }
  let n = 1;
  while (cells.length % 7 !== 0) {
    const date = new Date(y, m + 1, n);
    cells.push({ kind: 'other', date, ymd: toYmd(date) });
    n += 1;
  }
  return cells;
}

const WEEKDAYS_MON_FIRST = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

function ColorSwatches({ value, onChange, ariaLabel }) {
  return (
    <div className="haydi-color-swatches" role="group" aria-label={ariaLabel}>
      {HAYDI_ITEM_COLORS.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`haydi-color-swatch${value === c.hex ? ' haydi-color-swatch--active' : ''}`}
          style={{ '--swatch': c.hex }}
          onClick={() => onChange(c.hex)}
          aria-pressed={value === c.hex}
          title={c.id}
        />
      ))}
    </div>
  );
}

function CalendarTab({ user }) {
  const { t, i18n } = useTranslation();
  const [personal, setPersonal] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [formDraft, setFormDraft] = useState(() => defaultCalendarDraft(toYmd(new Date())));
  const [participants, setParticipants] = useState([]);
  const [users, setUsers] = useState([]);
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [selectedYmd, setSelectedYmd] = useState(() => toYmd(new Date()));
  const uid = user?.uid;

  const todayYmd = toYmd(new Date());

  const allEvents = useMemo(() => {
    return [...personal].sort((a, b) => eventSortKey(a) - eventSortKey(b));
  }, [personal]);

  const eventsByYmd = useMemo(() => {
    const map = {};
    for (const ev of allEvents) {
      const ymds = getYmdsForEventInMonth(ev, viewMonth);
      for (const ymd of ymds) {
        if (!map[ymd]) map[ymd] = [];
        map[ymd].push(ev);
      }
    }
    return map;
  }, [allEvents, viewMonth]);

  const selectedMonthDate = useMemo(() => {
    const [y, m, d] = selectedYmd.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [selectedYmd]);

  const selectedEvents = useMemo(() => {
    return allEvents
      .filter((ev) => getYmdsForEventInMonth(ev, selectedMonthDate).has(selectedYmd))
      .sort((a, b) => eventSortKey(a) - eventSortKey(b));
  }, [allEvents, selectedMonthDate, selectedYmd]);

  const monthCells = useMemo(() => buildMonthGrid(viewMonth), [viewMonth]);

  useEffect(() => {
    if (!uid) return;
    const q1 = query(
      collection(db, 'calendarEvents'),
      where('userId', '==', uid),
      where('isGlobal', '==', false),
    );
    const u1 = onSnapshot(q1, (snap) => {
      const rows = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
      rows.sort((a, b) => eventSortKey(a) - eventSortKey(b));
      setPersonal(rows);
    });
    (async () => {
      const us = await getDocs(collection(db, 'users'));
      setUsers(us.docs.map((d) => ({ _id: d.id, ...d.data() })));
    })();
    return () => {
      u1();
    };
  }, [uid]);

  const syncDraftToDay = useCallback((ymd) => {
    setFormDraft((prev) => ({
      ...defaultCalendarDraft(ymd),
      color: prev.color || DEFAULT_ITEM_COLOR,
    }));
  }, []);

  const onPickDay = useCallback(
    (cell) => {
      setSelectedYmd(cell.ymd);
      syncDraftToDay(cell.ymd);
      if (cell.kind === 'other') {
        setViewMonth(new Date(cell.date.getFullYear(), cell.date.getMonth(), 1));
      }
    },
    [syncDraftToDay],
  );

  const monthTitle = viewMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const handleAddAppointment = async () => {
    const combined = { ...formDraft, title: newTitle };
    if (!validateCalendarForm(combined) || !uid) return;
    if (formDraft.reminderBeforeStart === 'custom') {
      const c = parseInt(String(formDraft.reminderBeforeCustom).trim(), 10);
      if (!Number.isFinite(c) || c < 0) {
        window.alert(t('todoReminderBeforeInvalid'));
        return;
      }
    }
    let draftForBuild = combined;
    if (formDraft.reminderBeforeStart === 'custom') {
      const c = parseInt(String(formDraft.reminderBeforeCustom).trim(), 10);
      draftForBuild = { ...combined, reminderBeforeStart: String(c) };
    }
    const fields = buildCalendarEventFields(draftForBuild);
    await addDoc(collection(db, 'calendarEvents'), {
      title: newTitle.trim(),
      ...fields,
      userId: uid,
      isGlobal: false,
      participants,
      source: 'haydi',
      createdAt: serverTimestamp(),
    });
    setNewTitle('');
    setParticipants([]);
    syncDraftToDay(selectedYmd);
  };

  const removeEv = async (id) => {
    if (!window.confirm(t('confirmDeleteItem'))) return;
    await deleteDoc(doc(db, 'calendarEvents', id));
  };

  const goPrevMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));
  };
  const goNextMonth = () => {
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));
  };
  const goToday = () => {
    const n = new Date();
    setViewMonth(new Date(n.getFullYear(), n.getMonth(), 1));
    const y = toYmd(n);
    setSelectedYmd(y);
    syncDraftToDay(y);
  };

  const canSubmit =
    newTitle.trim() &&
    validateCalendarForm({ ...formDraft, title: newTitle });

  const recurrenceBlock = formDraft.recurrence !== 'none' && (
    <div className="haydi-cal-recurrence">
      <div className="haydi-cal-recurrence__legend">{t('calendarRecurrenceEnds')}</div>
      <div className="haydi-cal-recurrence__modes">
        <label className="haydi-cal-recurrence__mode">
          <input
            type="radio"
            name="recEnd"
            checked={formDraft.recurrenceEndMode !== 'count'}
            onChange={() => setFormDraft((f) => ({ ...f, recurrenceEndMode: 'date' }))}
          />
          {t('calendarRecurrenceEndDate')}
        </label>
        <label className="haydi-cal-recurrence__mode">
          <input
            type="radio"
            name="recEnd"
            checked={formDraft.recurrenceEndMode === 'count'}
            onChange={() => setFormDraft((f) => ({ ...f, recurrenceEndMode: 'count' }))}
          />
          {t('calendarRecurrenceEndAfter')}
        </label>
      </div>
      {formDraft.recurrenceEndMode === 'count' ? (
        <div className="haydi-field-compact">
          <label className="haydi-muted haydi-label-xs">{t('calendarRecurrenceCountLabel')}</label>
          <input
            type="number"
            min={1}
            max={500}
            className="haydi-input haydi-input--inline"
            value={formDraft.recurrenceCount}
            onChange={(e) => setFormDraft((f) => ({ ...f, recurrenceCount: e.target.value }))}
          />
        </div>
      ) : (
        <div className="haydi-field-compact">
          <label className="haydi-muted haydi-label-xs">{t('calendarRecurrenceUntil')}</label>
          <input
            type="date"
            className="haydi-input haydi-input--inline"
            value={formDraft.recurrenceUntil}
            onChange={(e) => setFormDraft((f) => ({ ...f, recurrenceUntil: e.target.value }))}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="haydi-cal-page">
      <header className="haydi-cal-page__hero">
        <h1 className="haydi-cal-page__title">{t('calendarTitle')}</h1>
        <p className="haydi-cal-page__lead">{t('calendarSubtitleMonth')}</p>
      </header>

      <div className="haydi-cal-layout">
        <section className="haydi-pro-panel haydi-pro-panel--elevated">
          <div className="haydi-pro-panel__head">
            <h2>{t('calendarMonthView')}</h2>
            <p className="haydi-pro-panel__meta">{t('calendarMonthHint')}</p>
          </div>
          <div className="haydi-pro-panel__body">
            <div className="haydi-cal-month__nav">
              <h3 className="haydi-cal-month__title">{monthTitle}</h3>
              <div className="haydi-cal-month__nav-btns">
                <button type="button" className="haydi-btn haydi-btn--secondary haydi-btn--sm" onClick={goPrevMonth}>
                  {t('calendarPrevMonth')}
                </button>
                <button type="button" className="haydi-btn haydi-btn--primary haydi-btn--sm" onClick={goToday}>
                  {t('calendarToday')}
                </button>
                <button type="button" className="haydi-btn haydi-btn--secondary haydi-btn--sm" onClick={goNextMonth}>
                  {t('calendarNextMonth')}
                </button>
              </div>
            </div>
            <div className="haydi-cal-month__dow" aria-hidden>
              {WEEKDAYS_MON_FIRST.map((k) => (
                <span key={k}>{t(`calendarDow_${k}`)}</span>
              ))}
            </div>
            <div className="haydi-cal-month__grid" role="grid" aria-label={t('calendarMonthView')}>
              {monthCells.map((cell) => {
                const evs = eventsByYmd[cell.ymd] || [];
                const isToday = cell.ymd === todayYmd;
                const isSel = cell.ymd === selectedYmd;
                const num = cell.date.getDate();
                const muted = cell.kind === 'other';
                return (
                  <button
                    key={`${cell.ymd}-${cell.kind}-${num}`}
                    type="button"
                    className={`haydi-cal-month__cell${muted ? ' haydi-cal-month__cell--muted' : ''}${isToday ? ' haydi-cal-month__cell--today' : ''}${isSel ? ' haydi-cal-month__cell--selected' : ''}`}
                    onClick={() => onPickDay(cell)}
                  >
                    <div className="haydi-cal-month__cell-num">{num}</div>
                    <div className="haydi-cal-month__cell-bars" aria-hidden>
                      {evs.slice(0, 5).map((ev) => (
                        <span
                          key={ev._id}
                          className="haydi-cal-month__bar"
                          style={{ background: eventColorHex(ev) }}
                        />
                      ))}
                    </div>
                    {evs.length > 5 && (
                      <span className="haydi-cal-month__more">{evs.length}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="haydi-cal-detail">
              <h4 className="haydi-cal-detail__title">
                {selectedYmd
                  ? t('calendarEventsOnDay', { date: selectedYmd })
                  : t('calendarSelectDay')}
              </h4>
              <ul className="haydi-cal-event-list">
                {selectedEvents.length === 0 && (
                  <li className="haydi-cal-event-list__empty">{t('calendarNoEventsDay')}</li>
                )}
                {selectedEvents.map((app) => {
                  const recLine = formatRecurrenceLine(app, t);
                  return (
                    <li
                      key={app._id}
                      className="haydi-cal-event-card"
                      style={{ '--ev-color': eventColorHex(app) }}
                    >
                      <div className="haydi-cal-event-card__main">
                        <div className="haydi-cal-event-card__title-row">
                          <span className="haydi-cal-event-card__title">{app.title}</span>
                          {app.source && String(app.source) !== 'haydi' && (
                            <span className="haydi-chip haydi-chip--xs">{app.source}</span>
                          )}
                        </div>
                        <div className="haydi-cal-event-card__when">{formatEventWhen(app, i18n.language)}</div>
                        {recLine && (
                          <div className="haydi-cal-event-card__rec">{recLine}</div>
                        )}
                        {app.location && (
                          <div className="haydi-cal-event-card__meta">{t('calendarLocation')}: {app.location}</div>
                        )}
                        {app.notes && (
                          <div className="haydi-cal-event-card__notes">{app.notes}</div>
                        )}
                        {formatCalendarReminderSummary(app, t) && (
                          <div className="haydi-cal-event-card__meta">
                            {t('calendarReminderLabel')}: {formatCalendarReminderSummary(app, t)}
                          </div>
                        )}
                        <div className="haydi-cal-event-card__meta">
                          {t('participants')}:{' '}
                          {(app.participants || [])
                            .map((pid) => users.find((u) => u._id === pid)?.name)
                            .filter(Boolean)
                            .join(', ') || (app.participants?.length ? '—' : t('calendarNoInvitees'))}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="haydi-btn haydi-btn--ghost haydi-btn--sm"
                        onClick={() => removeEv(app._id)}
                      >
                        {t('delete')}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>

        <div className="haydi-cal-sidebar">
          <section className="haydi-pro-panel haydi-pro-panel--compact">
            <div className="haydi-pro-panel__head">
              <h2>{t('calendarExternalCalendars')}</h2>
              <p className="haydi-pro-panel__meta">{t('calendarSyncHint')}</p>
            </div>
            <div className="haydi-pro-panel__body haydi-cal-sync">
              <div className="haydi-cal-sync__grid">
                <button type="button" className="haydi-cal-sync__btn" disabled title={t('calendarSyncComingSoon')}>
                  <span className="haydi-cal-sync__btn-icon" aria-hidden>G</span>
                  <div>
                    <strong>{t('calendarGoogleCal')}</strong>
                    <span>{t('calendarSyncComingSoon')}</span>
                  </div>
                </button>
                <button type="button" className="haydi-cal-sync__btn" disabled title={t('calendarSyncComingSoon')}>
                  <span className="haydi-cal-sync__btn-icon" aria-hidden></span>
                  <div>
                    <strong>{t('calendarAppleCal')}</strong>
                    <span>{t('calendarSyncComingSoon')}</span>
                  </div>
                </button>
                <button type="button" className="haydi-cal-sync__btn" disabled title={t('calendarSyncComingSoon')}>
                  <span className="haydi-cal-sync__btn-icon" aria-hidden>O</span>
                  <div>
                    <strong>{t('calendarOutlookCal')}</strong>
                    <span>{t('calendarSyncComingSoon')}</span>
                  </div>
                </button>
              </div>
            </div>
          </section>

          <section className="haydi-pro-panel haydi-pro-panel--elevated haydi-cal-composer-panel">
            <div className="haydi-pro-panel__head">
              <h2>{t('calendarAddEvent')}</h2>
              <p className="haydi-pro-panel__meta">{t('calendarPersonalHint')}</p>
            </div>
            <div className="haydi-pro-panel__body haydi-cal-composer">
              <label className="haydi-muted haydi-label-xs">{t('itemColor')}</label>
              <ColorSwatches
                value={formDraft.color || DEFAULT_ITEM_COLOR}
                onChange={(hex) => setFormDraft((f) => ({ ...f, color: hex }))}
                ariaLabel={t('itemColor')}
              />
              <label className="haydi-muted haydi-label-xs" style={{ marginTop: '0.75rem' }}>{t('appointmentTitle')}</label>
              <input
                type="text"
                className="haydi-input"
                placeholder={t('appointmentTitle')}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
              <label className="haydi-cal-composer__check">
                <input
                  type="checkbox"
                  checked={formDraft.allDay}
                  onChange={(e) => setFormDraft((f) => ({ ...f, allDay: e.target.checked }))}
                />
                {t('calendarAllDay')}
              </label>
              {formDraft.allDay ? (
                <div className="haydi-cal-composer__grid2">
                  <div>
                    <label className="haydi-muted haydi-label-xs">{t('calendarStartDate')}</label>
                    <input
                      type="date"
                      className="haydi-input"
                      value={formDraft.startDate}
                      onChange={(e) => setFormDraft((f) => ({ ...f, startDate: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="haydi-muted haydi-label-xs">{t('calendarEndDate')}</label>
                    <input
                      type="date"
                      className="haydi-input"
                      value={formDraft.endDate}
                      onChange={(e) => setFormDraft((f) => ({ ...f, endDate: e.target.value }))}
                    />
                  </div>
                </div>
              ) : (
                <div className="haydi-cal-composer__grid2">
                  <div>
                    <label className="haydi-muted haydi-label-xs">{t('calendarStartTime')}</label>
                    <input
                      type="datetime-local"
                      className="haydi-input"
                      value={formDraft.startDatetime}
                      onChange={(e) => setFormDraft((f) => ({ ...f, startDatetime: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="haydi-muted haydi-label-xs">{t('calendarEndTime')}</label>
                    <input
                      type="datetime-local"
                      className="haydi-input"
                      value={formDraft.endDatetime}
                      onChange={(e) => setFormDraft((f) => ({ ...f, endDatetime: e.target.value }))}
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="haydi-muted haydi-label-xs">{t('calendarLocation')}</label>
                <input
                  type="text"
                  className="haydi-input"
                  placeholder={t('calendarLocationPlaceholder')}
                  value={formDraft.location}
                  onChange={(e) => setFormDraft((f) => ({ ...f, location: e.target.value }))}
                />
              </div>
              <div>
                <label className="haydi-muted haydi-label-xs">{t('calendarNotes')}</label>
                <textarea
                  className="haydi-input"
                  placeholder={t('calendarNotesPlaceholder')}
                  value={formDraft.notes || ''}
                  onChange={(e) => setFormDraft((f) => ({ ...f, notes: e.target.value }))}
                  rows={4}
                />
              </div>
              <div>
                <label className="haydi-muted haydi-label-xs">{t('calendarRecurrence')}</label>
                <select
                  className="haydi-select haydi-input"
                  value={formDraft.recurrence}
                  onChange={(e) =>
                    setFormDraft((f) => ({ ...f, recurrence: e.target.value }))
                  }
                >
                  <option value="none">{t('calendarRecurrenceNone')}</option>
                  <option value="daily">{t('calendarRecurrenceDaily')}</option>
                  <option value="weekly">{t('calendarRecurrenceWeekly')}</option>
                  <option value="monthly">{t('calendarRecurrenceMonthly')}</option>
                  <option value="yearly">{t('calendarRecurrenceYearly')}</option>
                </select>
              </div>
              <div>
                <label className="haydi-muted haydi-label-xs">{t('calendarReminderLabel')}</label>
                <select
                  className="haydi-select haydi-input"
                  value={formDraft.reminderBeforeStart}
                  onChange={(e) =>
                    setFormDraft((f) => ({ ...f, reminderBeforeStart: e.target.value }))
                  }
                >
                  {CALENDAR_REMINDER_OFFSET_PRESETS.map((o) => (
                    <option key={o.value === '' ? 'none' : o.value} value={o.value}>
                      {t(o.labelKey)}
                    </option>
                  ))}
                </select>
                {formDraft.reminderBeforeStart === 'custom' && (
                  <div className="haydi-field-compact" style={{ marginTop: '0.5rem' }}>
                    <label className="haydi-muted haydi-label-xs">{t('reminderCustomMinutesLabel')}</label>
                    <input
                      type="number"
                      min={0}
                      max={10080}
                      className="haydi-input"
                      value={formDraft.reminderBeforeCustom}
                      onChange={(e) =>
                        setFormDraft((f) => ({ ...f, reminderBeforeCustom: e.target.value }))
                      }
                    />
                  </div>
                )}
                <p className="haydi-muted haydi-hint-xs">{t('calendarReminderHint')}</p>
              </div>
              {recurrenceBlock}
              <label className="haydi-muted haydi-label-xs">{t('participants')}</label>
              <p className="haydi-muted haydi-hint-xs">{t('calendarInviteOptional')}</p>
              <select
                multiple
                className="haydi-select haydi-input"
                value={participants}
                onChange={(e) => setParticipants([...e.target.selectedOptions].map((opt) => opt.value))}
                size={5}
              >
                {users.map((u) => (
                  <option value={u._id} key={u._id}>{u.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="haydi-btn haydi-btn--primary haydi-cal-composer__submit"
                onClick={handleAddAppointment}
                disabled={!canSubmit}
              >
                {t('add')}
              </button>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default CalendarTab;
